import {
	keepPreviousData,
	type QueryClient,
	queryOptions,
	useQuery,
} from "@tanstack/react-query"
import { GET } from "@/api/client"
import type { CompanyVesselsParams, MappingSummary } from "@/api/types"

export function companyOptions(companyId: number) {
	return queryOptions({
		queryKey: ["companies", companyId],
		queryFn: () =>
			GET("/api/v1/companies/{company_id}", {
				params: { path: { company_id: companyId } },
			}),
		enabled: companyId > 0,
	})
}

export function useCompany(companyId: number) {
	return useQuery(companyOptions(companyId))
}

export function companyMappingsOptions(companyId: number) {
	return queryOptions({
		queryKey: ["companies", companyId, "mappings"],
		queryFn: () =>
			GET("/api/v1/companies/{company_id}/mappings", {
				params: { path: { company_id: companyId } },
			}) as Promise<MappingSummary[]>,
		enabled: companyId > 0,
	})
}

export function useCompanyMappings(companyId: number) {
	return useQuery(companyMappingsOptions(companyId))
}

export async function getCompanyMapping(
	qc: QueryClient,
	companyId: number,
	system: MappingSummary["system"],
): Promise<MappingSummary | undefined> {
	const mappings = await qc.ensureQueryData(companyMappingsOptions(companyId))
	return mappings.find((m) => m.system === system)
}

export function companyContactsOptions(companyId: number) {
	return queryOptions({
		queryKey: ["companies", companyId, "contacts"],
		queryFn: () =>
			GET("/api/v1/companies/{company_id}/contacts", {
				params: { path: { company_id: companyId } },
			}),
		enabled: companyId > 0,
	})
}

export function useCompanyContacts(companyId: number) {
	return useQuery(companyContactsOptions(companyId))
}

export function companyVesselsOptions(
	companyId: number,
	params: Omit<CompanyVesselsParams, "companyId"> = {},
) {
	return queryOptions({
		queryKey: ["companies", companyId, "vessels", params],
		queryFn: () =>
			GET("/api/v1/companies/{company_id}/vessels", {
				params: {
					path: { company_id: companyId },
					query: params,
				},
			}),
		enabled: companyId > 0,
		placeholderData: keepPreviousData,
	})
}

export function companyEuetsOptions(companyId: number) {
	return queryOptions({
		queryKey: ["companies", companyId, "euets"],
		queryFn: () =>
			GET("/api/v1/companies/{company_id}/euets", {
				params: { path: { company_id: companyId } },
			}),
		enabled: companyId > 0,
	})
}

export function useCompanyEuets(companyId: number) {
	return useQuery(companyEuetsOptions(companyId))
}
